import React, { FC, useEffect, useState } from "react";
import { Button, Table } from "react-bootstrap";
import { notificationService } from "../../../../../shared/services/notification-service";
import { LeaveOnlineService } from "../../../../../shared/services/Report/LeaveOnline/leave-online";
import { AxiosError } from "axios";
import useSpinnerLoader from "../../../../../shared/components/spinner-loader";
import { initialResponseData } from "../../../../../shared/utils/constants";
import { apiEmployeeService } from "../../../../../shared/services/employee-service";
import { LeaveRequestService } from "../../../../../shared/services/leave-request";
import Moment from "moment";
import PaginationForm from "../../../../../shared/components/molecules/commons/pagination-form";
import { convertDateToString } from "../../../../../shared/utils/functions-date-time";
import InputSelect from "../../../../../shared/components/atoms/input-select";
import DateInput from "../../../../../shared/components/atoms/date-input";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { apiDropdownService } from "../../../../../shared/services/dropdown-service";
import { useParams } from "react-router-dom";

interface LeaveHistoryRowProps {
  request_date: string;
  approval_date: string;
  type_name: string;
  start_date: string;
  end_date: string;
  total_days: string;
  remaining_days: string;
}

type LeaveData = {
  type: string;
  used_days: number;
  total_days_allowed: number;
};

export type LeaveHistoryTypeTotal = {
  totalLeaveAmount: number;
  leaveByTypeTotal: LeaveData[];
  usedLeavePercentage: number;
};

const LeaveHistoryForm: FC = () => {
  const { id } = useParams();
  const [data, setData] = useState(initialResponseData);
  const [leaveTypeOptions, setLeaveTypeOptions] = useState<any>([]);
  const [leaveHistoryTypeTotal, setLeaveHistoryTypeTotal] = useState(
    {} as LeaveHistoryTypeTotal
  );
  const [leaveDate, setLeaveDate] = useState<any>({
    start_date: null,
    end_date: null,
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { loaderComponent, startLoading, stopLoading } = useSpinnerLoader();
  const [employeeData, setEmployeeData] = useState([]) as any;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [queryParams, setQueryParams] = useState({
    department_offices_name: "",
    division_group_office_name: "",
    structure_departments_name: "",
    position_name: "",
    leave_type: "",
    date_start: "",
    date_end: "",
    search: "",
    page: 1,
    page_size: 10,
  });

  const onChangePageSize = (page_size: number) => {
    setQueryParams({
      ...queryParams,
      page_size: page_size,
    });
  };

  const onChangePage = (page: number) => {
    setQueryParams({
      ...queryParams,
      page: page,
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.page_size, queryParams.page]);

  const fetchData = async () => {
    startLoading();
    try {
      const result = await LeaveOnlineService.getReportLeaveEachTypeList({
        ...queryParams,
        search: employeeData?.full_name_th,
      });
      setData(result);

      let userId = {
        user_id: employeeData?.user?.id,
      };

      const leaveHistoryTypeTotal =
        await LeaveRequestService.getLeaveHistoryTypeTotal({...userId,employee_id:id});
      if (leaveHistoryTypeTotal.length !== 0) {
        setLeaveHistoryTypeTotal(leaveHistoryTypeTotal);
      }
    } catch (error) {
      const err = error as AxiosError;
      notificationService.dangerNotification(
        "โหลดข้อมูลไม่สำเร็จ",
        err.message
      );
    } finally {
      stopLoading();
    }
  };

  const getUpdatedLeaveData = () => {
    // Map leave history details
    const leaveHistoryDetail =
      data?.rows?.map((history: any, index: number) => {
        // Find matching quota data for this leave type
        const quotaData = leaveHistoryTypeTotal?.leaveByTypeTotal?.find(
          (quota: any) => quota.type === history.leave_type_name
        );

        // Calculate used days up to this record's date
        const usedDaysUpToThisDate = data?.rows
          ?.slice(index)
          ?.filter((record: any) => 
            record.leave_type_name === history.leave_type_name &&
            new Date(record.end_date) <= new Date(history.end_date)
          )
          ?.reduce((sum: number, record: any) => 
            sum + parseFloat(record.total_hours_to_days), 0
          ) ?? 0;

        return {
          request_date: history.created_at,
          approval_date: history?.approver_at || "-",
          type_name: history.leave_type_name,
          start_date: history.start_date,
          end_date: history.end_date,
          total_days: history.total_hours_to_days,
          remaining_days:
            (quotaData?.total_days_allowed ?? 0) -
            usedDaysUpToThisDate,
        };
      }) || [];

    return leaveHistoryDetail;
  };

  const getEmployeeMe = async () => {
    let response = null;
    if (id) {
      response = await apiEmployeeService.getEmployeeDetail(Number(id));
    } else {
      response = await apiEmployeeService.getEmployeeMe();
    }
    if (response.length !== 0) {
      setEmployeeData(response);
    }
  };

  const handleDateInput = (date: Date | null, type: string) => {
    if (type === "start_date") {
      setLeaveDate((prev: Date) => ({
        ...prev,
        start_date: date ? `${convertDateToString(date)} 08:30:00` : null,
      }));

      setQueryParams({
        ...queryParams,
        date_start: date ? `${convertDateToString(date)} 08:30:00` : "",
      });
    }
    if (type === "end_date") {
      setLeaveDate((prev: Date) => ({
        ...prev,
        end_date: date ? `${convertDateToString(date)} 16:30:00` : null,
      }));

      setQueryParams({
        ...queryParams,
        date_end: date ? `${convertDateToString(date)} 16:30:00` : "",
      });
    }
  };

  const leaveRequestDropdown = async () => {
    const leaveRequestTypeOptions =
      await apiDropdownService.getLeaveRequestDropdown({});
    if (leaveRequestTypeOptions) {
      setLeaveTypeOptions(leaveRequestTypeOptions);
    }
  };

  useEffect(() => {
    leaveRequestDropdown();
    getEmployeeMe();
  }, []);

  useEffect(() => {
    if (employeeData?.user?.id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeData?.user?.id]);

  //   const onEdit = true;

  return (
    <>
      <div className="row mb-3">
        <div className="col-sm-12 col-md-3">
          <label className="form-label required">ประเภทการลา</label>
          <InputSelect
            options={leaveTypeOptions}
            placeholder="กรุณาเลือกประเภทการลา"
            name="is_active"
            onChange={(e) => {
              setQueryParams({
                ...queryParams,
                leave_type: e.value.value,
              });
            }}
            required={true}
            isLeaveRequestPage={true}
          />
        </div>

        <div className="col-sm-12 col-md-3">
          <div className={``}>
            <label className="form-label">วันเริ่ม</label>
            <DateInput
              date={leaveDate.start_date && new Date(leaveDate.start_date)}
              onChange={(e) => {
                handleDateInput(e, "start_date");
              }}
              required={true}
              minDate={undefined}
              maxDate={new Date()}
              disable={false}
            />
          </div>
        </div>
        <div className="col-sm-12 col-md-3 mt-sm-3 mt-md-0">
          <div className={``}>
            <label className="form-label">จนถึง</label>
            <DateInput
              date={leaveDate.end_date && new Date(leaveDate.end_date)}
              onChange={(e) => {
                handleDateInput(e, "end_date");
              }}
              required={true}
              minDate={
                leaveDate.start_date
                  ? new Date(leaveDate.start_date)
                  : undefined
              }
              maxDate={new Date()}
              disable={false}
            />
          </div>
        </div>
        <div className="col-sm-12 col-md-3 mt-sm-3 mt-md-0">
          <div className={``}>
            <label className="form-label"> &nbsp;</label>
            <Button
              variant="primary"
              type="button"
              className="d-block px-4"
              onClick={fetchData}
            >
              <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
            </Button>
          </div>
        </div>
      </div>

      <div className="overflow-auto">
        <Table bordered>
          <thead>
            <tr className="border-0">
              <th>วันที่ขอ</th>
              <th>วันที่อนุมัติ</th>
              <th>ประเภท</th>
              <th>เริ่ม</th>
              <th>สิ้นสุด</th>
              <th>วันลาทั้งหมด</th>
              <th>วันลาคงเหลือ</th>
            </tr>
          </thead>
          <tbody>
            {getUpdatedLeaveData().filter((data: any) => data.approval_date !== '-').map((data: any, index) => {
              return (
                <LeaveHistoryRow
                  key={index}
                  request_date={data.request_date}
                  approval_date={data.approval_date}
                  type_name={data.type_name}
                  start_date={data.start_date}
                  end_date={data.end_date}
                  total_days={data.total_days}
                  remaining_days={data.remaining_days}
                />
              );
            })}
          </tbody>
        </Table>
      </div>
      <div className="">
        <PaginationForm
          current_page={queryParams.page}
          count={data.count}
          page_size={queryParams.page_size}
          onChangePageSize={onChangePageSize}
          onChangePage={onChangePage}
        />
      </div>
    </>
  );
};

const LeaveHistoryRow: FC<LeaveHistoryRowProps> = ({
  request_date,
  approval_date,
  type_name,
  start_date,
  end_date,
  total_days,
  remaining_days,
}) => {
  return (
    <tr className="bg-white">
      <td>
        {request_date
          ? Moment(request_date)
              .utcOffset("-00:00")
              .format("DD/MM/YYYY HH:mm:ss")
          : "-"}
      </td>
      <td>
        {approval_date && approval_date !== "-"
          ? Moment(approval_date)
              .utcOffset("-00:00")
              .format("DD/MM/YYYY HH:mm:ss")
          : "-"}
      </td>
      <td>{type_name || "-"}</td>
      <td>
        {start_date
          ? Moment(start_date).utcOffset("-00:00").format("DD/MM/YYYY HH:mm:ss")
          : "-"}
      </td>
      <td>
        {end_date
          ? Moment(end_date).utcOffset("-00:00").format("DD/MM/YYYY HH:mm:ss")
          : "-"}
      </td>
      <td>{total_days || "-"}</td>
      <td>{remaining_days || "-"}</td>
    </tr>
  );
};

export default LeaveHistoryForm;
